import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, FormGroup, Label, Input, Button, Table, Col, Row, Card, CardBody, CardHeader, Container, Modal, ModalHeader, ModalBody } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { invoiceActions } from '../../sagas/invoiceSlice';
import Swal from 'sweetalert2';
import { formatPrice } from '../../helpers/utility';
import Select from "react-select";
import { clientActions } from '../../sagas/clientSlice';
import { productActions } from '../../sagas/productSlice';
import * as Yup from 'yup'; // Import Yup for validation
import { useFormik } from 'formik';
import { reglementActions } from '../../sagas/reglementSlice';
import CommandeLine from './CommandeLine';
import { FaArrowDown, FaArrowUp, FaBalanceScale, FaCalendarAlt, FaMoneyBillWave, FaShieldAlt } from 'react-icons/fa';
const InvoiceForm = ({ initialValues, onClose }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [formState, setForm] = useState(initialValues);
    const { isLoading, invoiceNumber } = useSelector(state => state.invoices);
    const unites = useSelector(status => status.products.unites);
    const clients = useSelector(state => state.clients.clients);
    const products = useSelector(state => state.products.products);
    const [selectedClient, setSelectedClient] = useState(null);
    const { loading: loadingReglements, error: errorReglements, echeances, cautions } = useSelector((state) => state.reglements);
    const [showEcheanceModal, setShowEcheanceModal] = useState(false);
    const [showCautionModal, setShowCautionModal] = useState(false);

    const [filters, setFilters] = useState({
        clientId: null,
        dateFrom: null,
        dateTo: null,
        statut: "EN_ATTENTE"
    });

    useEffect(() => {
        if (!formState?.numero) {
            setForm(prevForm => ({ ...prevForm, numero: invoiceNumber }));
        }
        dispatch(invoiceActions.fetchNewInvoiceNumber({companyId: formState?.companyId}));

        if (!clients || clients.length === 0) {
            dispatch(clientActions.search({
                query: ""
            }));
        }

        if (!products || products.length === 0) {
            dispatch(productActions.findAll({
                search: "",
                page: 0,
                size: 10,
            }));
        }
    }, [invoiceNumber]);

    // Validation schema
    const validationSchema = Yup.object().shape({
        clientId: Yup.number()
            .required(t('bonLivraison.clientRequired')), // Ensure a client is selected
        lignes: Yup.array().min(1, t('bonLivraison.atLeastOneItem')), // Ensure at least one item is added

    });

    const formik = useFormik({
        initialValues: {
            ...formState,
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            if (!values.dateEcheance) {
                Swal.fire({
                    icon: 'warning',
                    title: t('facture.validation.dueDateRequired'),
                    text: t('facture.validation.dueDateRequiredMessage'),
                });
                return;
            }

            if (!values.modeReglement) {
                Swal.fire({
                    icon: 'warning',
                    title: t('facture.validation.paymentMethodRequired'),
                    text: t('facture.validation.paymentMethodRequiredMessage'),
                });
                return;
            }

            Swal.fire({
                title: t('facture.confirmSubmit'),
                text: t('facture.confirmSubmitMessage'),
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: t('common.yes'),
                cancelButtonText: t('common.no'),
            }).then((result) => {
                if (result.isConfirmed) {
                    let payload = {
                        invoice: {
                            ...values,
                            modeReglement: values.modeReglement === "-1" ? null : values.modeReglement,
                            statut: 'IMPAYE'
                        },
                        onSuccess: () => {
                            Swal.fire({
                                icon: 'success',
                                title: t('facture.invoiceSaved'),
                                text: t('facture.invoiceSavedMessage'),
                            });
                            formik.resetForm();
                            onClose();
                        },
                    };
                    dispatch(invoiceActions.addInvoice(payload));
                }
            });
        }
    });

    const { values, getFieldProps, handleSubmit, setFieldValue, errors, touched } = formik;

    const clientOptions = clients?.map(client => ({
        solde: client.solde,
        totalDebit: client.totalDebit,
        totalCredit: client.totalCredit,
        value: client.id,
        label: client.typeClient === 'PARTICULIER'
            ? `${client.nom} ${client.prenom}`
            : client.entreprise,
    })) ?? [];

    // Calculate totals
    const montantTotalTaxe = values.montantTotalTaxe || 0;
    const montantTotalTTC = values.montantTotalTTC || 0;
    const montantTotalHT = values.montantTotalHT || 0

    const getTotalMontant = (items) => {
        return items.reduce((total, item) => total + item.montantRegle, 0);
    };

    return (
        <Container fluid>
            <Form onSubmit={handleSubmit} onAbort={onClose}>
                <Card>
                    <CardHeader>
                        <Row>
                            <Col md="3">
                                <FormGroup>
                                    <Label>{t('facture.invoiceNumber')}</Label>
                                    <Input
                                        type="text"
                                        value={values.numero || ''}
                                        disabled
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="3">
                                <Label htmlFor="clientId">{t('bonLivraison.client')}:</Label>
                                <Select
                                    id="clientId"
                                    options={clientOptions}
                                    value={clientOptions.find(option => option.value === values.clientId)}
                                    onChange={(selectedOption) => {
                                        setFieldValue('clientId', selectedOption.value);
                                        setSelectedClient(selectedOption);
                                        dispatch(reglementActions.echeanceClient({ ...filters, clientId: selectedOption.value }));
                                        dispatch(reglementActions.cautionClient({ ...filters, clientId: selectedOption.value }));
                                        setFilters(f => ({ ...f, clientId: selectedOption.value }));
                                    }}
                                    placeholder={t('bonLivraison.selectClient')}
                                    isSearchable
                                    menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                                {errors.clientId && touched.clientId && (
                                    <div className="text-danger small mt-1">{errors.clientId}</div>
                                )}
                            </Col>
                            <Col md="3">
                                <FormGroup>
                                    <Label>{t('facture.invoiceDate')}</Label>
                                    <Input
                                        type="date"
                                        name="dateFacture"
                                        {...getFieldProps('dateFacture')}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="3">
                                <Label>{t('facture.dueDate')}</Label>
                                <Input
                                    type="date"
                                    id="dateEcheance"
                                    {...getFieldProps('dateEcheance')}
                                    className="form-control"
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col md={3}>
                                <FormGroup>
                                    <Label for="modeReglement">
                                        <FaMoneyBillWave className="me-1" />
                                        {t('facture.modeReglement')} *
                                    </Label>
                                    <Input
                                        type="select"
                                        id="modeReglement"
                                        name="modeReglement"
                                        {...getFieldProps('modeReglement')}
                                    >
                                        <option value="">{t('common.select')}</option>
                                        <option value="ESPECES">{t('facture.modeReglement.espece')}</option>
                                        <option value="CHEQUE">{t('facture.modeReglement.cheque')}</option>
                                        <option value="VIREMENT">{t('facture.modeReglement.virement')}</option>
                                        <option value="CARTE_BANCAIRE">{t('facture.modeReglement.card')}</option>
                                        <option value="TRAITE">{t('facture.modeReglement.traite')}</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>
                        {selectedClient && (
                            <Row className="mt-2">
                                <Col>
                                    <Row className="g-3 align-items-stretch">
                                        <Col xs={12} md={2}>
                                            <Card className="shadow-sm h-100 bg-gradient bg-light border-0">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-3">
                                                    <FaBalanceScale className="mb-2 text-success" size={28} />
                                                    <span className="text-muted text-uppercase small mb-1">{t('client.balance')}</span>
                                                    {selectedClient.totalCredit > selectedClient.totalDebit ? (
                                                        <span className="fw-bold fs-5 text-success">+{formatPrice(Math.abs(selectedClient.solde))}</span>
                                                    ) : selectedClient.totalCredit < selectedClient.totalDebit ? (
                                                        <span className="fw-bold fs-5 text-danger">-{formatPrice(Math.abs(selectedClient.solde))}</span>
                                                    ) : (
                                                        <span className="fw-bold fs-5 text-muted">{formatPrice(0)}</span>
                                                    )}
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col xs={12} md={2}>
                                            <Card className="shadow-sm h-100 bg-gradient bg-light border-0">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-3">
                                                    <FaArrowDown className="mb-2 text-danger" size={28} />
                                                    <span className="text-muted text-uppercase small mb-1">{t('client.totalDebit')}</span>
                                                    <span className="fw-bold fs-5">{formatPrice(selectedClient.totalDebit)}</span>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col xs={12} md={2}>
                                            <Card className="shadow-sm h-100 bg-gradient bg-light border-0">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-3">
                                                    <FaArrowUp className="mb-2 text-primary" size={28} />
                                                    <span className="text-muted text-uppercase small mb-1">{t('client.totalCredit')}</span>
                                                    <span className="fw-bold fs-5">{formatPrice(selectedClient.totalCredit)}</span>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col xs={12} md={3}>
                                            <Card className="shadow-sm h-100 bg-gradient bg-warning bg-opacity-10 border-0">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-3">
                                                    <FaCalendarAlt className="mb-2 text-warning" size={28} />
                                                    <span className="text-muted text-uppercase small mb-1">{t('client.echeances')}</span>
                                                    <span className="fw-bold fs-5 text-warning">{echeances?.length || 0}</span>
                                                    <Button color="link" size="sm" className="mt-2 px-2 py-0" onClick={() => setShowEcheanceModal(true)}>
                                                        {t('client.viewDetails')}
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col xs={12} md={3}>
                                            <Card className="shadow-sm h-100 bg-gradient bg-info bg-opacity-10 border-0">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-3">
                                                    <FaShieldAlt className="mb-2 text-info" size={28} />
                                                    <span className="text-muted text-uppercase small mb-1">{t('client.cautions')}</span>
                                                    <span className="fw-bold fs-5 text-info">{cautions?.length || 0}</span>
                                                    <Button color="link" size="sm" className="mt-2 px-2 py-0" onClick={() => setShowCautionModal(true)}>
                                                        {t('client.viewDetails')}
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        )}
                    </CardHeader>
                    <CardBody>
                        <Row>
                            <CommandeLine formik={formik} />
                        </Row>
                        {errors.lignes && touched.lignes && (
                            <div className="text-danger small mt-1">{errors.lignes}</div>
                        )}
                        <Row className="mt-3 justify-content-end">
                            <Col md="4" className="text-end">
                                <div className="mb-2">
                                    <h5>{t('devis.totalHT')}: {montantTotalHT} DH</h5>
                                </div>
                                <div className="mb-2">
                                    <h5>{t('devis.totalTaxe')}: {montantTotalTaxe} DH</h5>
                                </div>
                                <div>
                                    <h5>{t('devis.totalTTC')}: {montantTotalTTC} DH</h5>
                                </div>
                            </Col>
                        </Row>
                        <div className="mt-4 d-flex justify-content-end">
                            <Button color="secondary" onClick={onClose} className="me-2">
                                {t('common.cancel')}
                            </Button>
                            <Button color="primary" type="submit" disabled={isLoading}>
                                {isLoading ? t('common.saving') : t('common.save')}
                            </Button>
                        </div>
                    </CardBody>
                </Card>
            </Form>

            <Modal isOpen={showEcheanceModal} toggle={() => setShowEcheanceModal(false)} size="lg">
                <ModalHeader toggle={() => setShowEcheanceModal(false)} className="bg-warning bg-opacity-10 border-0">
                    <FaCalendarAlt className="me-2 text-warning" />
                    <span className="fw-bold text-warning">{t('client.echeancesDetails')}</span>
                </ModalHeader>
                <ModalBody>
                    {echeances && echeances.length > 0 ? (
                        <div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-warning fs-5">
                                    <FaCalendarAlt className="me-2" /> {t('client.echeances')}: {echeances.length}
                                </span>
                                <span className="fw-bold text-muted">{t('echeance.total')}: <span className="text-warning">{formatPrice(getTotalMontant(echeances))}</span></span>
                            </div>
                            <Table bordered hover responsive className="table-sm">
                                <thead className="bg-warning bg-opacity-10">
                                    <tr>
                                        <th>{t('echeance.reference')}</th>
                                        <th>{t('echeance.dateReglement')}</th>
                                        <th>{t('echeance.numeroCheque')}</th>
                                        <th>{t('echeance.banque')}</th>
                                        <th>{t('echeance.montantCheque')}</th>
                                        <th>{t('echeance.dateEcheance')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {echeances.map((echeance, idx) => (
                                        <tr key={idx}>
                                            <td>{echeance.reference}</td>
                                            <td>{echeance.dateReglement}</td>
                                            <td>{echeance.numeroCheque}</td>
                                            <td>{echeance.banque}</td>
                                            <td className="text-end fw-bold text-warning">{formatPrice(echeance.montantRegle)}</td>
                                            <td>{echeance.dateEcheance}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-muted text-center py-4">{t('client.noEcheances')}</div>
                    )}
                </ModalBody>
            </Modal>

            <Modal isOpen={showCautionModal} toggle={() => setShowCautionModal(false)} size="lg">
                <ModalHeader toggle={() => setShowCautionModal(false)} className="bg-info bg-opacity-10 border-0">
                    <FaShieldAlt className="me-2 text-info" />
                    <span className="fw-bold text-info">{t('client.cautionsDetails')}</span>
                </ModalHeader>
                <ModalBody>
                    {cautions && cautions.length > 0 ? (
                        <div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-info fs-5">
                                    <FaShieldAlt className="me-2" /> {t('client.cautions')}: {cautions.length}
                                </span>
                                <span className="fw-bold text-muted">{t('caution.total')}: <span className="text-info">{formatPrice(getTotalMontant(cautions))}</span></span>
                            </div>
                            <Table bordered hover responsive className="table-sm">
                                <thead className="bg-info bg-opacity-10">
                                    <tr>
                                        <th>{t('caution.reference')}</th>
                                        <th>{t('caution.dateReglement')}</th>
                                        <th>{t('caution.numeroCheque')}</th>
                                        <th>{t('caution.banque')}</th>
                                        <th>{t('caution.montantCheque')}</th>
                                        <th>{t('caution.dateEcheance')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cautions.map((caution, idx) => (
                                        <tr key={idx}>
                                            <td>{caution.reference}</td>
                                            <td>{caution.dateReglement}</td>
                                            <td>{caution.numeroCheque}</td>
                                            <td>{caution.banque}</td>
                                            <td className="text-end fw-bold text-info">{formatPrice(caution.montantRegle)}</td>
                                            <td>{caution.dateEcheance}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-muted text-center py-4">{t('client.noCautions')}</div>
                    )}
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default InvoiceForm;
